/**
 * Anteprima dei task scritti una riga per uno nella finestra Nuovo task
 * (7/10/2026). Il testo lo divide il divisore (packages/shared, dividiTesto):
 * qui si vede cosa si creerà, riga per riga, e si corregge prima del tasto
 * solo "Crea N task".
 *
 * Ogni riga: la spunta (si crea o no), il titolo pulito e quattro etichette
 * che si toccano: cliente, chi lo fa, scadenza, priorità. Quello che la riga
 * non dice prende il valore della finestra ("Assegnato a", "Scadenza",
 * "Cliente") ed è scritto in grigio. I dubbi del divisore stanno sotto la
 * riga: niente viene scelto in silenzio.
 *
 * Computer: un clic sull'etichetta apre un menu piccolo con le stesse scelte
 * delle tendine. Telefono: la riga è come quelle del kit (MRow) con la spunta
 * a sinistra; toccandola sale "Questo task" con Cliente, Chi lo fa, Scadenza
 * e Priorità, che aprono MScelte o MCampo (os-planning/mobile.md §12).
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { DUBBIO_MANCA_COSA_FARE, DUBBIO_TITOLO_ACCORCIATO, TITOLO_MAX, type TaskDiviso } from '@masamune/shared';
import { Icon, type IconName } from '../ui/Icon';
import { MobileSheet } from '../ui/MobileSheet';
import { MCampo, MRow, MScelte, MSearch } from '../mobile/MobileKit';
import type { CorpoTask } from '../../features/tasks/useTaskActions';
import './nuovo-task.css';

// ── I dati di una riga ────────────────────────────────────────────────────

/** Da dove viene un valore: letto nella riga, preso dalla finestra, scelto a mano, o niente. */
export type Origine = 'riga' | 'finestra' | 'scelto' | 'nessuno';

/** Le correzioni fatte a mano su una riga: vincono su quello che ha letto il divisore. */
export interface Scelte {
  spuntata?: boolean;
  clientId?: string | null;
  assigneeId?: string | null;
  dueDate?: string | null;
  alta?: boolean;
}

export interface RigaAnteprima {
  chiave: string;
  d: TaskDiviso;
  spuntata: boolean;
  clientId: string | null;
  clienteDa: Origine;
  assigneeId: string | null;
  personaDa: Origine;
  dueDate: string | null;
  dataDa: Origine;
  alta: boolean;
  /** Quello che il server rifiuterebbe: così la riga non si può creare. */
  problema: string | null;
}

/** "Assegnato a", "Scadenza" e "Cliente" della finestra: valgono per le righe che non li dicono. */
export interface Predefiniti { clientId: string | null; assigneeId: string | null; dueDate: string | null }

export interface OpzioniAnteprima {
  /** 'YYYY-MM-DD' di Roma. */
  oggi: string;
  meId: string;
  /** nome intero (negli elenchi da scegliere) e breve (nelle etichette e nella riga del telefono). */
  clienti: Array<{ id: string; nome: string; breve: string }>;
  /** nome intero ("Andrea Anfuso (io)") e breve ("A. Anfuso") per la riga del telefono. */
  persone: Array<{ id: string; nome: string; breve: string }>;
}

/** Una chiave per riga che regge quando si scrive sopra o sotto: il testo, e quante volte è già comparso. */
function chiaviDi(divisi: TaskDiviso[]): string[] {
  const viste = new Map<string, number>();
  return divisi.map((d) => {
    const n = viste.get(d.testo) ?? 0;
    viste.set(d.testo, n + 1);
    return `${d.testo}#${n}`;
  });
}

function valore<T>(scelto: T | null | undefined, riga: T | null, finestra: T | null): [T | null, Origine] {
  if (scelto !== undefined) return [scelto, 'scelto'];
  if (riga != null) return [riga, 'riga'];
  if (finestra != null) return [finestra, 'finestra'];
  return [null, 'nessuno'];
}

export function righeAnteprima(divisi: TaskDiviso[], scelte: Record<string, Scelte>, pred: Predefiniti): RigaAnteprima[] {
  const chiavi = chiaviDi(divisi);
  return divisi.map((d, i) => {
    const chiave = chiavi[i]!;
    const s = scelte[chiave] ?? {};
    const [clientId, clienteDa] = valore(s.clientId, d.clientId, pred.clientId);
    const [assigneeId, personaDa] = valore(s.assigneeId, d.assigneeId, pred.assigneeId);
    const [dueDate, dataDa] = valore(s.dueDate, d.dueDate, pred.dueDate);
    // Lo schema del server rifiuta tutto il lotto per un titolo vuoto o oltre 200 caratteri:
    // qui si vede prima, sulla riga, e il tasto resta spento.
    const titolo = d.titolo.trim();
    const problema = !titolo ? 'Manca cosa fare' : titolo.length > TITOLO_MAX ? `Troppo lunga: al massimo ${TITOLO_MAX} caratteri` : null;
    return {
      chiave, d, clientId, clienteDa, assigneeId, personaDa, dueDate, dataDa,
      alta: s.alta ?? d.alta,
      // Le righe che dicono solo una data o una persona ("venerdì" da solo) partono senza spunta.
      spuntata: !problema && (s.spuntata ?? !d.dubbi.includes(DUBBIO_MANCA_COSA_FARE)),
      problema,
    };
  });
}

/** Il corpo di POST /tasks/bulk per una riga. Se il titolo è stato accorciato, la riga intera va nella descrizione. */
export function corpoDi(r: RigaAnteprima, projectId: string | null): CorpoTask {
  return {
    title: r.d.titolo.trim(),
    description: r.d.dubbi.includes(DUBBIO_TITOLO_ACCORCIATO) ? r.d.testo : null,
    projectId,
    clientId: r.clientId,
    status: 'TODO',
    priority: r.alta ? 'ALTA' : 'MEDIA',
    assigneeId: r.assigneeId,
    dueDate: r.dueDate,
    remindAt: null,
    recurrence: 'NONE',
    isPrivate: false,
  };
}

// ── Date e nomi ───────────────────────────────────────────────────────────

const FMT_GIORNO = new Intl.DateTimeFormat('it-IT', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
/** "ven 9 ott". */
export const giornoCorto = (iso: string) => FMT_GIORNO.format(new Date(`${iso}T00:00:00Z`)).replace(/\./g, '');
function piuGiorni(iso: string, n: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
const maiuscola = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const piega = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();

interface Opzione { v: string; l: string; sub?: string }

/** Oggi, domani e i sei giorni dopo: le scadenze che si scelgono di più. */
function scelteDiData(oggi: string): Opzione[] {
  const domani = piuGiorni(oggi, 1);
  return [
    { v: oggi, l: 'Oggi', sub: giornoCorto(oggi) },
    { v: domani, l: 'Domani', sub: giornoCorto(domani) },
    ...[2, 3, 4, 5, 6, 7].map((n) => {
      const v = piuGiorni(oggi, n);
      return { v, l: maiuscola(giornoCorto(v)) };
    }),
  ];
}

// Forme societarie in fondo al nome, tolte una alla volta ("… Societa' a Responsabilita' Limitata Semplificata").
const FORMA_IN_FONDO = /[\s,]+(?:s\.?\s?r\.?\s?l\.?(?:\s?s\.?)?|s\.?\s?p\.?\s?a\.?|s\.?\s?n\.?\s?c\.?|s\.?\s?a\.?\s?s\.?|s\.?\s?s\.?|b\.?\s?v\.?|o[uü]|a\s+s\.?\s?u\.?|[&e]\s*c\.?|societ[aà]'?|cooperativa|benefit|semplificata|limitata|responsabilit[aà]'?|a|-)$/i;

/**
 * Il nome che sta in un'etichetta o nella riga del telefono: senza la forma
 * societaria e senza il titolare. "Montagna comunicazione di Angelica
 * Montagna" → "Montagna comunicazione", "Azienda Agricola Graci di Aiello
 * Graci Alberto S.s." → "Azienda Agricola Graci". "Dottoressa Ylenia di
 * Franco" resta com'è: dopo "di" c'è un cognome, non un titolare.
 */
export function nomeBreve(nome: string): string {
  const senzaForme = (s: string) => {
    let t = s.trim();
    for (let giro = 0; giro < 8 && FORMA_IN_FONDO.test(t); giro++) t = t.replace(FORMA_IN_FONDO, '').trim();
    return t;
  };
  let n = senzaForme(nome);
  const titolare = /^(.+?\S)\s+di\s+\S+\s+\S+.*$/i.exec(n);
  if (titolare) n = senzaForme(titolare[1]!);
  return n || nome;
}

const nomeCliente = (opz: OpzioniAnteprima, id: string | null) => (id ? opz.clienti.find((c) => c.id === id)?.nome ?? 'Cliente' : null);
const breveCliente = (opz: OpzioniAnteprima, id: string | null) => (id ? opz.clienti.find((c) => c.id === id)?.breve ?? 'Cliente' : null);
const nomePersona = (opz: OpzioniAnteprima, id: string | null) => (id ? opz.persone.find((p) => p.id === id) ?? null : null);

/** Il grigio dice "preso dalla finestra": vale per le righe che non lo dicono. */
const DALLA_FINESTRA = 'Preso dalla finestra: vale per le righe che non lo dicono';

// ── Computer: un menu piccolo sotto l'etichetta ──────────────────────────

/**
 * Il menu sta in un portale su <body>, fisso sullo schermo: dentro la finestra,
 * che scorre, verrebbe tagliato dal bordo. Si chiude con un clic fuori, con
 * Esc (senza chiudere la finestra sotto) o se la finestra scorre.
 */
function Menu({ ancora, onClose, children }: { ancora: HTMLElement; onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top?: number; bottom?: number; left: number; maxHeight: number } | null>(null);
  const LARGO = 260;
  useLayoutEffect(() => {
    const r = ancora.getBoundingClientRect();
    const sotto = window.innerHeight - r.bottom - 16;
    const sopra = r.top - 16;
    const left = Math.max(8, Math.min(r.left, window.innerWidth - LARGO - 8));
    if (sotto >= 280 || sotto >= sopra) setPos({ top: r.bottom + 6, left, maxHeight: Math.max(160, sotto) });
    else setPos({ bottom: window.innerHeight - r.top + 6, left, maxHeight: Math.max(160, sopra) });
  }, [ancora]);
  useEffect(() => {
    const fuori = (e: Event) => {
      if (e.target instanceof Node && ref.current?.contains(e.target)) return;
      onClose();
    };
    window.addEventListener('resize', onClose);
    window.addEventListener('scroll', fuori, true);
    return () => {
      window.removeEventListener('resize', onClose);
      window.removeEventListener('scroll', fuori, true);
    };
  }, [onClose]);
  useEffect(() => {
    if (!pos) return;
    const primo = ref.current?.querySelector<HTMLElement>('input, [aria-selected="true"], button');
    primo?.focus();
  }, [pos]);
  if (!pos) return null;
  return createPortal(
    <>
      <div className="nt-menu-velo" onMouseDown={(e) => { e.preventDefault(); onClose(); }} />
      <div
        ref={ref}
        className="nt-menu"
        role="dialog"
        style={{ top: pos.top, bottom: pos.bottom, left: pos.left, width: LARGO, maxHeight: pos.maxHeight }}
        onKeyDown={(e) => {
          if (e.key !== 'Escape') return;
          e.stopPropagation();
          onClose();
          ancora.focus();
        }}
      >
        {children}
      </div>
    </>,
    document.body,
  );
}

function ListaMenu({ opzioni, valore, onScegli, cerca, vuoto, piede }: {
  opzioni: Opzione[];
  valore: string;
  onScegli: (v: string) => void;
  cerca?: string;
  vuoto?: string;
  piede?: ReactNode;
}) {
  const [q, setQ] = useState('');
  const filtrate = q.trim() ? opzioni.filter((o) => piega(o.l).includes(piega(q.trim()))) : opzioni;
  return (
    <>
      {cerca && (
        <div className="nt-menu-cerca">
          <input
            className="inp"
            placeholder={cerca}
            aria-label={cerca}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && filtrate[0]) { e.preventDefault(); onScegli(filtrate[0].v); } }}
          />
        </div>
      )}
      <div className="nt-menu-lista" role="listbox">
        {filtrate.map((o) => (
          <button key={o.v || 'nessuno'} type="button" role="option" aria-selected={o.v === valore} className="nt-menu-voce" onClick={() => onScegli(o.v)}>
            <span>{o.l}</span>
            {o.sub && <em>{o.sub}</em>}
            {o.v === valore && <Icon name="check" size={12} />}
          </button>
        ))}
        {!filtrate.length && <div className="nt-menu-vuoto">{vuoto ?? 'Niente con questo nome'}</div>}
      </div>
      {piede}
    </>
  );
}

type Campo = 'cliente' | 'persona' | 'data';

function Etichetta({ icona, testo, intero, origine, vuota, onClick, aperta, nome }: {
  icona: IconName;
  testo: string;
  /** Il valore intero, nel suggerimento, quando l'etichetta ne mostra uno breve. */
  intero?: string;
  origine: Origine;
  vuota: boolean;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  aperta: boolean;
  nome: string;
}) {
  const cls = `nt-chip${vuota ? ' is-vuota' : origine === 'finestra' ? ' is-finestra' : ''}`;
  const tutto = intero ?? testo;
  return (
    <button
      type="button"
      className={cls}
      onClick={onClick}
      aria-haspopup="dialog"
      aria-expanded={aperta}
      aria-label={`${nome}: ${tutto}${origine === 'finestra' ? ', preso dalla finestra' : ''}`}
      title={origine === 'finestra' ? `${tutto}. ${DALLA_FINESTRA}` : tutto}
    >
      <Icon name={icona} size={11} />
      <span>{testo}</span>
    </button>
  );
}

function RigaComputer({ r, opz, onCambia }: { r: RigaAnteprima; opz: OpzioniAnteprima; onCambia: (s: Scelte) => void }) {
  const [menu, setMenu] = useState<{ campo: Campo; ancora: HTMLElement } | null>(null);
  const chiudi = useCallback(() => setMenu(null), []);
  const apri = (campo: Campo) => (e: React.MouseEvent<HTMLButtonElement>) => {
    const ancora = e.currentTarget;
    setMenu((m) => (m?.campo === campo ? null : { campo, ancora }));
  };
  const scegli = (s: Scelte) => { onCambia(s); setMenu(null); };
  const cliente = breveCliente(opz, r.clientId);
  const persona = nomePersona(opz, r.assigneeId);
  const dubbi = [...(r.problema ? [r.problema] : []), ...r.d.dubbi];
  return (
    <div className={`nt-riga${r.spuntata ? '' : ' is-spenta'}`}>
      <label className="nt-spunta" title={r.spuntata ? 'Si crea' : 'Non si crea'}>
        <input
          type="checkbox"
          checked={r.spuntata}
          disabled={!!r.problema && !r.spuntata}
          onChange={(e) => onCambia({ spuntata: e.target.checked })}
          aria-label={`Crea "${r.d.titolo}"`}
        />
      </label>
      <div className="nt-riga-corpo">
        <div className="nt-riga-titolo">{r.d.titolo || r.d.testo}</div>
        <div className="nt-etichette">
          <Etichetta nome="Cliente" icona="building" testo={cliente ?? 'Nessun cliente'} intero={nomeCliente(opz, r.clientId) ?? undefined} origine={r.clienteDa} vuota={!cliente} aperta={menu?.campo === 'cliente'} onClick={apri('cliente')} />
          <Etichetta nome="Chi lo fa" icona="user" testo={persona?.nome ?? 'Nessuno'} origine={r.personaDa} vuota={!persona} aperta={menu?.campo === 'persona'} onClick={apri('persona')} />
          <Etichetta nome="Scadenza" icona="calendar" testo={r.dueDate ? giornoCorto(r.dueDate) : 'Senza scadenza'} origine={r.dataDa} vuota={!r.dueDate} aperta={menu?.campo === 'data'} onClick={apri('data')} />
          <button
            type="button"
            className={`nt-chip nt-chip-alta${r.alta ? '' : ' is-vuota'}`}
            aria-pressed={r.alta}
            aria-label={r.alta ? 'Priorità alta: tocca per tornare normale' : 'Priorità normale: tocca per renderla alta'}
            onClick={() => onCambia({ alta: !r.alta })}
          >
            <Icon name="arrowUp" size={11} />
            <span>{r.alta ? 'Alta' : 'Normale'}</span>
          </button>
        </div>
        {dubbi.map((d) => (
          <div key={d} className={`nt-dubbio${d === r.problema ? ' is-blocco' : ''}`}>{d}</div>
        ))}
      </div>
      {menu && (
        <Menu ancora={menu.ancora} onClose={chiudi}>
          {menu.campo === 'cliente' && (
            <ListaMenu
              cerca="Cerca un cliente"
              vuoto="Nessun cliente con questo nome"
              opzioni={[{ v: '', l: 'Nessun cliente' }, ...opz.clienti.map((c) => ({ v: c.id, l: c.nome }))]}
              valore={r.clientId ?? ''}
              onScegli={(v) => scegli({ clientId: v || null })}
            />
          )}
          {menu.campo === 'persona' && (
            <ListaMenu
              opzioni={[{ v: '', l: 'Nessuno' }, ...opz.persone.map((p) => ({ v: p.id, l: p.nome }))]}
              valore={r.assigneeId ?? ''}
              onScegli={(v) => scegli({ assigneeId: v || null })}
            />
          )}
          {menu.campo === 'data' && (
            <ListaMenu
              opzioni={[{ v: '', l: 'Senza scadenza' }, ...scelteDiData(opz.oggi)]}
              valore={r.dueDate ?? ''}
              onScegli={(v) => scegli({ dueDate: v || null })}
              piede={(
                <label className="nt-menu-data">
                  <span>Un altro giorno</span>
                  <input
                    className="inp mono"
                    type="date"
                    defaultValue={r.dueDate ?? ''}
                    onChange={(e) => { if (e.target.value) scegli({ dueDate: e.target.value }); }}
                  />
                </label>
              )}
            />
          )}
        </Menu>
      )}
    </div>
  );
}

// ── Telefono: la riga del kit con la spunta, e "Questo task" dal basso ──────

function RigaTelefono({ r, opz, onCambia, onApri }: {
  r: RigaAnteprima;
  opz: OpzioniAnteprima;
  onCambia: (s: Scelte) => void;
  onApri: () => void;
}) {
  const persona = nomePersona(opz, r.assigneeId);
  const pezzi: Array<{ t: string; finestra: boolean }> = [];
  const cliente = breveCliente(opz, r.clientId);
  if (cliente) pezzi.push({ t: cliente, finestra: r.clienteDa === 'finestra' });
  if (persona) pezzi.push({ t: persona.breve, finestra: r.personaDa === 'finestra' });
  if (r.dueDate) pezzi.push({ t: giornoCorto(r.dueDate), finestra: r.dataDa === 'finestra' });
  if (r.alta) pezzi.push({ t: 'Alta', finestra: false });
  const dubbi = [...(r.problema ? [r.problema] : []), ...r.d.dubbi];
  return (
    <div className={`nt-m-riga${r.spuntata ? '' : ' is-spenta'}`}>
      <button
        type="button"
        className="nt-m-spunta"
        role="checkbox"
        aria-checked={r.spuntata}
        aria-label={r.spuntata ? `Non creare "${r.d.titolo}"` : `Crea "${r.d.titolo}"`}
        disabled={!!r.problema && !r.spuntata}
        onClick={() => onCambia({ spuntata: !r.spuntata })}
      >
        <span>{r.spuntata && <Icon name="check" size={14} strokeWidth={2.5} />}</span>
      </button>
      <button type="button" className="mk-row nt-m-corpo" onClick={onApri}>
        <span className="mk-main">
          <span className="mk-title">{r.d.titolo || r.d.testo}</span>
          <span className="mk-sub">
            {pezzi.length
              ? pezzi.map((p, i) => <span key={i} className={p.finestra ? 'nt-m-finestra' : 'is-ink'}>{i > 0 && ' · '}{p.t}</span>)
              : 'Senza cliente, persona e scadenza'}
          </span>
          {dubbi.length > 0 && <span className="nt-m-dubbio">{dubbi[0]}{dubbi.length > 1 ? ` (e altri ${dubbi.length - 1})` : ''}</span>}
        </span>
        <Icon name="chevronR" size={13} className="mk-chev" />
      </button>
    </div>
  );
}

function FoglioClienti({ open, onClose, opz, valore, onScegli }: {
  open: boolean;
  onClose: () => void;
  opz: OpzioniAnteprima;
  valore: string;
  onScegli: (v: string) => void;
}) {
  const [q, setQ] = useState('');
  useEffect(() => { if (open) setQ(''); }, [open]);
  const tutti: Opzione[] = [{ v: '', l: 'Nessun cliente' }, ...opz.clienti.map((c) => ({ v: c.id, l: c.nome }))];
  const filtrate = q.trim() ? tutti.filter((o) => piega(o.l).includes(piega(q.trim()))) : tutti;
  return (
    <MobileSheet open={open} onClose={onClose} title="Cliente">
      <div className="nt-m-cerca"><MSearch value={q} onChange={setQ} placeholder="Cerca un cliente" /></div>
      <div className="mk-list">
        {filtrate.map((o) => (
          <MRow key={o.v || 'nessuno'} title={o.l} trail={o.v === valore ? <Icon name="check" size={16} /> : undefined} chevron={false} onClick={() => onScegli(o.v)} />
        ))}
        {!filtrate.length && <div className="mk-empty">Nessun cliente con questo nome.</div>}
      </div>
    </MobileSheet>
  );
}

type Vista = 'riga' | 'cliente' | 'persona' | 'data' | 'altra-data' | 'priorita';

function QuestoTask({ r, opz, onCambia, onClose }: {
  r: RigaAnteprima;
  opz: OpzioniAnteprima;
  onCambia: (s: Scelte) => void;
  onClose: () => void;
}) {
  const [vista, setVista] = useState<Vista>('riga');
  const [altraData, setAltraData] = useState(r.dueDate ?? opz.oggi);
  const torna = useCallback(() => setVista('riga'), []);
  const cliente = nomeCliente(opz, r.clientId);
  const persona = nomePersona(opz, r.assigneeId);
  const dubbi = [...(r.problema ? [r.problema] : []), ...r.d.dubbi];
  const grigio = r.clienteDa === 'finestra' || r.personaDa === 'finestra' || r.dataDa === 'finestra';
  const valoreRiga = (testo: string | null, origine: Origine, vuoto: string) => (
    <span className={`nt-m-valore${!testo ? ' is-vuoto' : origine === 'finestra' ? ' is-finestra' : ''}`}>{testo ?? vuoto}</span>
  );
  return (
    <>
      <MobileSheet
        open
        onClose={onClose}
        title="Questo task"
        footer={(
          <button type="button" className="btn m-block" disabled={!!r.problema && !r.spuntata} onClick={() => { onCambia({ spuntata: !r.spuntata }); onClose(); }}>
            {r.spuntata ? 'Non crearlo' : 'Crealo'}
          </button>
        )}
      >
        <div className="nt-m-foglio">
          <div className="nt-m-foglio-titolo">{r.d.titolo || r.d.testo}</div>
          {dubbi.map((d) => <div key={d} className={`nt-dubbio${d === r.problema ? ' is-blocco' : ''}`}>{d}</div>)}
          <div className="mk-list">
            <MRow title="Cliente" trail={valoreRiga(cliente, r.clienteDa, 'Nessuno')} onClick={() => setVista('cliente')} />
            <MRow title="Chi lo fa" trail={valoreRiga(persona?.nome ?? null, r.personaDa, 'Nessuno')} onClick={() => setVista('persona')} />
            <MRow title="Scadenza" trail={valoreRiga(r.dueDate ? giornoCorto(r.dueDate) : null, r.dataDa, 'Nessuna')} onClick={() => setVista('data')} />
            <MRow title="Priorità" trail={valoreRiga(r.alta ? 'Alta' : 'Normale', 'riga', '')} onClick={() => setVista('priorita')} />
          </div>
          {grigio && <div className="mk-group-note">In grigio quello preso dalla finestra: vale per le righe che non lo dicono.</div>}
        </div>
      </MobileSheet>
      <FoglioClienti open={vista === 'cliente'} onClose={torna} opz={opz} valore={r.clientId ?? ''} onScegli={(v) => { onCambia({ clientId: v || null }); torna(); }} />
      <MScelte
        open={vista === 'persona'}
        onClose={torna}
        titolo="Chi lo fa"
        opzioni={[{ v: '', l: 'Nessuno' }, ...opz.persone.map((p) => ({ v: p.id, l: p.nome }))]}
        valore={r.assigneeId ?? ''}
        onScegli={(v) => { onCambia({ assigneeId: v || null }); torna(); }}
      />
      <MScelte
        open={vista === 'data'}
        onClose={torna}
        titolo="Scadenza"
        opzioni={[{ v: '', l: 'Senza scadenza' }, ...scelteDiData(opz.oggi), { v: 'altra', l: 'Un altro giorno' }]}
        valore={r.dueDate ?? ''}
        onScegli={(v) => {
          if (v === 'altra') { setAltraData(r.dueDate ?? opz.oggi); setVista('altra-data'); return; }
          onCambia({ dueDate: v || null });
          torna();
        }}
      />
      <MCampo
        open={vista === 'altra-data'}
        onClose={torna}
        titolo="Scadenza"
        campo={<input className="inp" type="date" aria-label="Scadenza" value={altraData} onChange={(e) => setAltraData(e.target.value)} />}
        onSalva={() => { if (altraData) onCambia({ dueDate: altraData }); torna(); }}
      />
      <MScelte
        open={vista === 'priorita'}
        onClose={torna}
        titolo="Priorità"
        opzioni={[{ v: 'MEDIA', l: 'Normale' }, { v: 'ALTA', l: 'Alta' }]}
        valore={r.alta ? 'ALTA' : 'MEDIA'}
        onScegli={(v) => { onCambia({ alta: v === 'ALTA' }); torna(); }}
      />
    </>
  );
}

// ── L'anteprima intera ────────────────────────────────────────────────────

export function AnteprimaTask({ righe, opz, mobile, onCambia }: {
  righe: RigaAnteprima[];
  opz: OpzioniAnteprima;
  mobile: boolean;
  /** Una correzione su una riga (chiave), oppure su tutte (null). */
  onCambia: (chiave: string | null, s: Scelte) => void;
}) {
  const [aperta, setAperta] = useState<string | null>(null);
  const rigaAperta = aperta ? righe.find((r) => r.chiave === aperta) ?? null : null;
  const spuntate = righe.filter((r) => r.spuntata).length;
  const daControllare = righe.filter((r) => r.problema || r.d.dubbi.length).length;
  const tutte = spuntate === righe.length;
  return (
    <section className="nt-anteprima" aria-label="I task che si creano">
      <div className="nt-anteprima-hd">
        <span>
          <strong>{tutte ? `${righe.length} task da creare` : `${spuntate} task da creare su ${righe.length}`}</strong>
          {daControllare > 0 && <span className="nt-anteprima-dubbi"> · {daControllare === 1 ? 'uno' : daControllare} da controllare</span>}
        </span>
        <button type="button" className="nt-tutte" onClick={() => onCambia(null, { spuntata: !tutte })}>
          {tutte ? 'Togli tutte le spunte' : 'Spunta tutte'}
        </button>
      </div>
      {mobile ? (
        <div className="nt-m-lista">
          {righe.map((r) => (
            <RigaTelefono key={r.chiave} r={r} opz={opz} onCambia={(s) => onCambia(r.chiave, s)} onApri={() => setAperta(r.chiave)} />
          ))}
        </div>
      ) : (
        <div className="nt-lista">
          {righe.map((r) => <RigaComputer key={r.chiave} r={r} opz={opz} onCambia={(s) => onCambia(r.chiave, s)} />)}
        </div>
      )}
      {mobile && rigaAperta && (
        <QuestoTask r={rigaAperta} opz={opz} onCambia={(s) => onCambia(rigaAperta.chiave, s)} onClose={() => setAperta(null)} />
      )}
    </section>
  );
}
